/**
 * Authentication and permission validation utilities
 * Ported from PHP LibAuthentication.php
 */

/**
 * Get restricted pages configuration
 * @returns {Object} Map of page identifiers to their permission requirements
 */
export const getRestrictedPages = () => {
  return {
    // Contabilidade menu
    'accounting:statistics': { types: [1, 2, 9, 10] }, // Admin, Client, Super Admin
    'accounting:turno': { types: [1, 2, 9, 10] },
    'accounting:consumption/vehicles': { types: [1, 2, 9, 10], combustivel: true },
    'accounting:stocks': { types: [1, 2, 9, 10] },
    'accounting:estimates': { types: [1, 2, 9, 10], combustivel: true },
    'accounting:anmc': { types: [1, 2, 9, 10], combustivel: true },
    'accounting:saft': { types: [1, 2, 9, 10] },
    
    // Ferramentas menu
    'tools:clients': { types: [1, 3, 9, 10] },
    'tools:customers': { types: [1, 3, 9, 10] },
    'tools:providers': { types: [1, 3, 9, 10] },
    'documents:payments': { types: [1, 3, 9, 10] },
    'tools:partners': { types: [1, 3, 9, 10] },
    'tools:products': { types: [1, 3, 9, 10] },
    'tools:agenda': { types: [1, 3, 4, 9, 10] },
    'tools:calendar': { types: [1, 3, 4, 9, 10] },
    'tools:downloads': { types: [1, 3, 9, 10] },
    'tools:services': { types: [1, 3, 9, 10] },
    'tools:tourniquet': { types: [1, 3, 9, 10] },
    
    // Documentos menu
    'documents:orders': { types: [1, 3, 9, 10] },
    'documents:guias': { types: [1, 3, 9, 10] },
    'documents:sales': { types: [1, 3, 9, 10] },
    'documents:tickets': { types: [1, 3, 9, 10] },
    'documents:cc': { types: [1, 3, 9, 10] },
    
    // Configuração menu
    'config:pricetags': { types: [1, 9, 10] },
    'config:taxes': { types: [1, 9, 10], combustivel: true },
    'config:prices_combustivel': { types: [1, 9, 10], combustivel: true },
    'config:nozzles': { types: [1, 9, 10], combustivel: true },
    'config:series': { types: [1, 9, 10] },
    'config:veeder_root': { types: [1, 9, 10], combustivel: true },
    'config:employees': { types: [1, 9, 10] },
    'config:categories': { types: [1, 9, 10] },
    'config:segmentacao': { types: [1, 9, 10] },
    'config:zones': { types: [1, 3, 9, 10] },
    
    // Administração menu
    'admin:users_management': { types: [10] },
    'admin:pos_admin': { types: [10] },
    'admin:site_admin': { types: [10] },
    'admin:menu_admin': { types: [10] },
    'admin:anulados': { types: [10] },

    // POS menu
    'pos:customers': { types: [1, 3, 4, 9, 10] },
    'pos:tickets': { types: [1, 3, 4, 9, 10] },
    'pos:inventory': { types: [1, 3, 4, 9, 10] },
    
    // Other pages
    'assistencias': { types: [1, 3, 9, 10] },
  };
};

/**
 * Get modules map - list of all module-based permissions
 * @returns {Array} Array of module identifiers
 */
export const getModulesMap = () => {
  return [
    'accounting:statistics',
    'accounting:turno',
    'accounting:consumption/vehicles',
    'accounting:stocks',
    'accounting:estimates',
    'accounting:anmc',
    'accounting:saft',
    
    'tools:clients',
    'tools:customers',
    'tools:providers',
    'documents:payments',
    'tools:partners',
    'tools:products',
    'tools:agenda',
    'tools:calendar',
    'tools:downloads',
    'tools:services',
    'tools:tourniquet',

    'documents:orders',
    'documents:guias',
    'documents:sales',
    'documents:tickets',
    'documents:cc',

    'config:pricetags',
    'config:taxes',
    'config:prices_combustivel',
    'config:nozzles',
    'config:series',
    'config:veeder_root',
    'config:employees',
    'config:categories',
    'config:segmentacao',
    'config:zones',

    'pos:customers',
    'pos:tickets',
    'pos:inventory',
  ];
};

/**
 * Validate permissions for a specific page based on user modules
 * @param {string} page - The page identifier to check permissions for
 * @param {Array} modules - Array of modules the user has access to
 * @returns {boolean} True if user has permission, false otherwise
 */
export const validatePermissions = (page, modules = []) => {
  const modulesMap = getModulesMap();
  
  if (!modulesMap.includes(page)) {
    return true;
  }
  
  return modules.includes(page);
};

/**
 * Unified permission validation function that checks both modules and user types
 * @param {string} page - The page identifier to check permissions for
 * @param {Array} modules - Array of modules the user has access to
 * @param {number} userType - The user type (1=Admin, 2=Client, 3=Employee, 4=Limited, 10=Super Admin)
 * @param {boolean} combustivel - Whether the company is a fuel station
 * @returns {boolean} True if user has permission, false otherwise
 */
export const hasPermission = (page, modules = [], userType = 0, combustivel = false) => {
  const pageMap = {
    'statistics': 'accounting:statistics',
    'statistics/statistics_empresa': 'accounting:statistics',
    'turno': 'accounting:turno',
    'turno/turno_empresa': 'accounting:turno',
    'anmc': 'accounting:anmc',
    'anmc/anmc_empresa': 'accounting:anmc',
    'saft': 'accounting:saft',
    'saft/saft_empresa': 'accounting:saft',
    'consumption/vehicles': 'accounting:consumption/vehicles',
    'stocks': 'accounting:stocks',
    'estimates': 'accounting:estimates',
    
    'downloads': 'tools:downloads',
    'users_management': 'admin:users_management',
    'users_management/insert_user': 'admin:users_management',
    'users_management/mudapassword': 'admin:users_management',
    'users_management/repor_backups': 'admin:users_management',
    'pos_admin': 'admin:pos_admin',
    'site_admin': 'admin:site_admin',
    'menu_admin': 'admin:menu_admin',
  };
  
  let permissionKey = page;
  if (pageMap[page]) {
    permissionKey = pageMap[page];
  }

  // First check using module-based permissions
  if (!validatePermissions(permissionKey, modules)) {
    return false;
  }
  
  // Then check using user type-based permissions
  const restrictedPages = getRestrictedPages();
  
  if (!restrictedPages[permissionKey]) {
    return true; // If page not in restricted list, allow access
  }
  
  // Check if user type is allowed for this page
  if (restrictedPages[permissionKey].types.includes(userType)) {
    // Check combustivel requirement if specified
    if (restrictedPages[permissionKey].combustivel && !combustivel) {
      return false;
    }
    return true;
  }
  
  return false;
};

/**
 * Check if user has any permission within a specific module
 * @param {string} module - The module identifier to check (e.g., 'accounting')
 * @param {Array} modules - Array of modules the user has access to
 * @param {number} userType - The user type (1=Admin, 2=Client, 3=Employee, 4=Limited, 10=Super Admin)
 * @param {boolean} combustivel - Whether the company is a fuel station
 * @returns {boolean} True if user has at least one permission in the module, false otherwise
 */
export const hasModulePermission = (module, modules = [], userType = 0, combustivel = false) => {
  const restrictedPages = getRestrictedPages();
  
  // Find all pages that start with the module prefix
  const modulePages = Object.keys(restrictedPages).filter(page => 
    page.startsWith(`${module}:`)
  );
  
  // Check if user has permission for any of the module pages
  for (const page of modulePages) {
    if (hasPermission(page, modules, userType, combustivel)) {
      return true;
    }
  }
  
  return false;
};

/**
 * Check if user has permission for a specific page
 * This is the main function to be used in components
 * @param {string} page - The page identifier to check
 * @param {Object} context - User context object containing userType, modules, combustivel
 * @returns {boolean} True if user has permission, false otherwise
 */
export const hasMenuPermission = (page, context) => {
  const { memberType = 0, fullEmpresa = {}, combustivel = false } = context;
  
  // Get modules from empresa
  const modules = fullEmpresa.modules ? fullEmpresa.modules.split(';').filter(m => m) : [];
  
  // Special case for 'accounting' - check if user has any accounting permission
  if (page === 'accounting') {
    return hasModulePermission('accounting', modules, memberType, combustivel);
  } else if (page === 'tools') {
    return hasModulePermission('tools', modules, memberType, combustivel);
  } else if (page === 'documents') {
    return hasModulePermission('documents', modules, memberType, combustivel);
  } else if (page === 'config') {
    return hasModulePermission('config', modules, memberType, combustivel);
  } else if (page === 'pos') {
    return hasModulePermission('pos', modules, memberType, combustivel);
  }
  
  // Use the unified permission validation function
  return hasPermission(page, modules, memberType, combustivel);
};

